import React, { useState, useEffect } from 'react';
import {
    AccountCash,
    KeyStat,
    CfdOrderRequest,
    ConciseMarketData,
    CfdSummaryResponse,
} from '../../api';
import { Header } from '@cloudscape-design/components';
import {trading212AccountApi, trading212CFDApi} from '../../client/clients';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export const Trading212Page: React.FC = () => {
    useDocumentTitle('Trading 212');
    const [ticker, setTicker] = useState<string>('');
    const [value, setValue] = useState<string>('');
    const [keyStats, setKeyStats] = useState<KeyStat[]>([]);
    const [stockPrice, setStockPrice] = useState<ConciseMarketData | null>(null);
    const [accountCash, setAccountCash] = useState<AccountCash | null>(null);
    const [cfdSummary, setCfdSummary] = useState<CfdSummaryResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [accountCashLoading, setAccountCashLoading] = useState<boolean>(false);
    const [placeOrderLoading, setPlaceOrderLoading] = useState<boolean>(false);
    const [orderResponse, setOrderResponse] = useState<any>(null);
    const [error, setError] = useState<string>('');
    const [accountCashError, setAccountCashError] = useState<string>('');
    const [cfdError, setCfdError] = useState<string>('');
    const [placeOrderError, setPlaceOrderError] = useState<string>('');

    // CFD-specific state
    const [cfdQuantity, setCfdQuantity] = useState<string>('');
    const [cfdOrderLoading, setCfdOrderLoading] = useState<boolean>(false);
    const [cfdOrderResponse, setCfdOrderResponse] = useState<any>(null);
    const [cfdOrderError, setCfdOrderError] = useState<string>('');

    const handleFetchStockData = async () => {
        if (!ticker.trim()) {
            setError('Please enter a ticker symbol');
            return;
        }

        setLoading(true);
        setError('');
        setKeyStats([]);
        setStockPrice(null);
        setOrderResponse(null); // Auto-close order response when selecting new stock
        
        try {
            // Call both APIs in parallel
            const [keyStatsResponse, stockPriceResponse] = await Promise.allSettled([
                trading212CFDApi.getKeyStats(ticker.trim().toUpperCase()),
                trading212AccountApi.getStockPrice1(ticker.trim().toUpperCase())
            ]);

            // Handle key stats response
            if (keyStatsResponse.status === 'fulfilled') {
                setKeyStats(keyStatsResponse.value.data);
            } else {
                console.error('Error fetching key stats:', keyStatsResponse.reason);
                setError(prev => prev ? `${prev}\nFailed to fetch key stats: ${keyStatsResponse.reason?.response?.data?.message || keyStatsResponse.reason?.message}` 
                    : `Failed to fetch key stats: ${keyStatsResponse.reason?.response?.data?.message || keyStatsResponse.reason?.message}`);
            }

            // Handle stock price response  
            if (stockPriceResponse.status === 'fulfilled') {
                setStockPrice(stockPriceResponse.value.data);
            } else {
                console.error('Error fetching stock price:', stockPriceResponse.reason);
                setError(prev => prev ? `${prev}\nFailed to fetch stock price: ${stockPriceResponse.reason?.response?.data?.message || stockPriceResponse.reason?.message}`
                    : `Failed to fetch stock price: ${stockPriceResponse.reason?.response?.data?.message || stockPriceResponse.reason?.message}`);
            }

            // If both failed, show a general error
            if (keyStatsResponse.status === 'rejected' && stockPriceResponse.status === 'rejected') {
                setError('Failed to fetch both key stats and stock price data');
            }

        } catch (err: any) {
            console.error('Unexpected error:', err);
            setError(`Unexpected error: ${err.message}`);
            setKeyStats([]);
            setStockPrice(null);
        } finally {
            setLoading(false);
        }
    };

    const handleFetchAccountCash = async () => {
        setAccountCashLoading(true);
        setAccountCashError('');
        setCfdError('');
        
        try {
            // Fetch both ISA and CFD data in parallel
            const [isaResponse, cfdResponse] = await Promise.allSettled([
                trading212AccountApi.getAccountCash(),
                trading212CFDApi.getAccountsSummary()
            ]);

            // Handle ISA response
            if (isaResponse.status === 'fulfilled') {
                setAccountCash(isaResponse.value.data);
            } else {
                console.error('Error fetching ISA account cash:', isaResponse.reason);
                setAccountCashError(`Failed to fetch ISA account cash: ${isaResponse.reason?.response?.data?.message || isaResponse.reason?.message}`);
                setAccountCash(null);
            }

            // Handle CFD response
            if (cfdResponse.status === 'fulfilled') {
                setCfdSummary(cfdResponse.value.data);
            } else {
                console.error('Error fetching CFD summary:', cfdResponse.reason);
                setCfdError(`Failed to fetch CFD summary: ${cfdResponse.reason?.response?.data?.message || cfdResponse.reason?.message}`);
                setCfdSummary(null);
            }
        } catch (err: any) {
            console.error('Error fetching account data:', err);
            setAccountCashError(`Failed to fetch account data: ${err.message}`);
            setAccountCash(null);
            setCfdSummary(null);
        } finally {
            setAccountCashLoading(false);
        }
    };

    const handlePlaceOrder = async () => {
        if (!ticker.trim()) {
            setPlaceOrderError('Please enter a ticker symbol');
            return;
        }

        if (!value.trim() || isNaN(Number(value)) || Number(value) <= 0) {
            setPlaceOrderError('Please enter a valid order value');
            return;
        }

        setPlaceOrderLoading(true);
        setPlaceOrderError('');
        setOrderResponse(null); // Clear previous response
        
        try {
            const response = await trading212AccountApi.placeValueOrder1({ ticker: ticker.trim().toUpperCase(), value: Number(value) });
            console.log('Order placed successfully:', response.data);
            setOrderResponse(response.data);
        } catch (err: any) {
            console.error('Error placing order:', err);
            setPlaceOrderError(`Failed to place order: ${err.response?.data?.message || err.message}`);
        } finally {
            setPlaceOrderLoading(false);
        }
    };

    const handleFractionOrder = async (fraction: number, fractionLabel: string) => {
        if (!ticker.trim()) {
            setPlaceOrderError('Please enter a ticker symbol');
            return;
        }

        setPlaceOrderLoading(true);
        setPlaceOrderError('');
        setOrderResponse(null); // Clear previous response
        
        try {
            // First refresh account cash to get the latest balance
            console.log(`Refreshing account cash for ${fractionLabel} order...`);
            const accountResponse = await trading212AccountApi.getAccountCash();
            setAccountCash(accountResponse.data);
            
            // Calculate the order value based on fraction of free cash
            const freeCash = accountResponse.data.free || 0;
            const orderValue = Math.round((freeCash * fraction) * 100) / 100; // Round to 2 decimal places
            
            if (orderValue <= 0) {
                setPlaceOrderError(`Cannot place ${fractionLabel} order: insufficient free cash (£${freeCash.toFixed(2)})`);
                return;
            }

            console.log(`Placing ${fractionLabel} order: £${orderValue.toFixed(2)} of £${freeCash.toFixed(2)} free cash`);
            
            // Place the order
            const response = await trading212AccountApi.placeValueOrder1({ ticker: ticker.trim().toUpperCase(), value: orderValue });
            console.log(`${fractionLabel} order placed successfully:`, response.data);
            setOrderResponse(response.data);
            
            // Update the value input to show the amount that was ordered
            setValue(orderValue.toString());
            
        } catch (err: any) {
            console.error(`Error placing ${fractionLabel} order:`, err);
            setPlaceOrderError(`Failed to place ${fractionLabel} order: ${err.response?.data?.message || err.message}`);
        } finally {
            setPlaceOrderLoading(false);
        }
    };

    const handleCfdPlaceOrder = async () => {
        if (!ticker.trim()) {
            setCfdOrderError('Please enter a ticker symbol');
            return;
        }

        if (!cfdQuantity.trim() || isNaN(Number(cfdQuantity)) || Number(cfdQuantity) <= 0) {
            setCfdOrderError('Please enter a valid quantity');
            return;
        }

        setCfdOrderLoading(true);
        setCfdOrderError('');
        setCfdOrderResponse(null);
        
        try {
            // First get stock price
            console.log('Getting stock price for CFD order...');
            const {data: stockPriceResponse} = await trading212AccountApi.getStockPrice1(ticker.trim().toUpperCase());

            const targetPrice = (stockPriceResponse as ConciseMarketData).close || 0;
            if (targetPrice <= 0) {
                setCfdOrderError('Invalid stock price received');
                return;
            }

            // Then place CFD order
            const orderRequest: CfdOrderRequest = {
                instrumentCode: ticker.trim().toUpperCase(),
                targetPrice: targetPrice,
                quantity: Number(cfdQuantity)
            };

            console.log('Placing CFD order:', orderRequest);
            const response = await trading212CFDApi.openPosition(orderRequest);
            console.log('CFD order placed successfully:', response);
            setCfdOrderResponse(response);
            
        } catch (err: any) {
            console.error('Error placing CFD order:', err);
            setCfdOrderError(`Failed to place CFD order: ${err.response?.data?.message || err.message}`);
        } finally {
            setCfdOrderLoading(false);
        }
    };

    const handleCfdFractionOrder = async (fraction: number, fractionLabel: string) => {
        if (!ticker.trim()) {
            setCfdOrderError('Please enter a ticker symbol');
            return;
        }

        setCfdOrderLoading(true);
        setCfdOrderError('');
        setCfdOrderResponse(null);
        
        try {
            // First get CFD settings to determine max buy amount
            console.log(`Getting CFD settings for ${fractionLabel} order...`);
            const {data: settingsResponse} = await trading212CFDApi.getInstrumentSettings(ticker.trim().toUpperCase());
            
            if (!settingsResponse) {
                setCfdOrderError(`No CFD settings found for ${ticker.trim().toUpperCase()}`);
                return;
            }
            if (!settingsResponse.maxBuyValue || settingsResponse.maxBuyValue < 5) {
                setCfdOrderError(`Max buy value is undefined or too small ${ticker.trim().toUpperCase()}`);
                return;
            }

            const maxBuyAmount = settingsResponse.maxBuyValue * 0.99; // Use 99% of maxBuy
            const orderQuantity = Math.round((maxBuyAmount * fraction) * 100) / 100;
            
            if (orderQuantity <= 0) {
                setCfdOrderError(`Cannot place ${fractionLabel} CFD order: insufficient max buy amount`);
                return;
            }

            console.log(`Calculated ${fractionLabel} order quantity: ${orderQuantity} of max ${maxBuyAmount}`);

            // Get stock price
            const {data: stockPriceResponse} = await trading212AccountApi.getStockPrice1(ticker.trim().toUpperCase());
            const targetPrice = (stockPriceResponse as ConciseMarketData).close || 0;
            
            if (targetPrice <= 0) {
                setCfdOrderError('Invalid stock price received');
                return;
            }

            // Place CFD order
            const orderRequest: CfdOrderRequest = {
                instrumentCode: ticker.trim().toUpperCase(),
                targetPrice: targetPrice,
                quantity: orderQuantity
            };

            console.log(`Placing ${fractionLabel} CFD order:`, orderRequest);
            const response = await trading212CFDApi.openPosition(orderRequest);
            console.log(`${fractionLabel} CFD order placed successfully:`, response);
            setCfdOrderResponse(response);
            
            // Update the quantity input to show the amount that was ordered
            setCfdQuantity(orderQuantity.toString());
            
        } catch (err: any) {
            console.error(`Error placing ${fractionLabel} CFD order:`, err);
            setCfdOrderError(`Failed to place ${fractionLabel} CFD order: ${err.response?.data?.message || err.message}`);
        } finally {
            setCfdOrderLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleFetchStockData();
        }
    };

    const formatValue = (value: KeyStat['value']): string => {
        if (!value || value.value === undefined || value.value === null) {
            return 'N/A';
        }

        let displayValue = value.value.toString();
        
        // Format currency values
        if (value.currency) {
            displayValue = `${value.currency} ${value.value.toLocaleString()}`;
        } else if (value.type === 'percentage') {
            displayValue = `${value.value}%`;
        } else if (typeof value.value === 'number') {
            displayValue = value.value.toLocaleString();
        }

        return displayValue;
    };

    // Load account cash automatically when component mounts
    useEffect(() => {
        handleFetchAccountCash();
    }, []);

    return (
        <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ marginBottom: '30px' }}>
                <Header variant="h1">Trading 212</Header>
            </div>

            {/* Account Overview Section */}
            <div style={{ marginBottom: '40px' }}>
                {/* Header with refresh button */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                    <h2 style={{ color: '#333', fontSize: '20px', margin: 0 }}>Account Overview</h2>
                    <button
                        onClick={handleFetchAccountCash}
                        disabled={accountCashLoading}
                        style={{
                            backgroundColor: accountCashLoading ? '#ccc' : '#ffc107',
                            color: accountCashLoading ? 'white' : 'black',
                            border: 'none',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            cursor: accountCashLoading ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        title="Refresh Both ISA and CFD Account Data"
                    >
                        {accountCashLoading ? '⟳' : '↻'}
                    </button>
                </div>

                {/* Account Cash Error */}
                {accountCashError && (
                    <div style={{
                        marginBottom: '20px',
                        padding: '15px',
                        backgroundColor: '#f8d7da',
                        color: '#721c24',
                        border: '1px solid #f5c6cb',
                        borderRadius: '4px',
                        fontSize: '14px'
                    }}>
                        <strong>Account Cash Error:</strong> {accountCashError}
                    </div>
                )}

                {/* Side-by-side layout for ISA and CFD */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', clear: 'both' }}>
                    {/* ISA Account Display */}
                    <div style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            padding: '10px 20px',
                            fontWeight: 'bold',
                            textAlign: 'center'
                        }}>
                            ISA Account
                        </div>
                        {accountCash ? (
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                                gap: '15px',
                                padding: '20px'
                            }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Free Cash</div>
                                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#28a745' }}>
                                        {accountCash.currency} {accountCash.free?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Total Cash</div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#007bff' }}>
                                        {accountCash.currency} {accountCash.total?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>P&L</div>
                                    <div style={{ 
                                        fontSize: '14px', 
                                        fontWeight: '600', 
                                        color: (accountCash.ppl || 0) >= 0 ? '#28a745' : '#dc3545' 
                                    }}>
                                        {accountCash.currency} {accountCash.ppl?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Invested</div>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                                        {accountCash.currency} {accountCash.invested?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Result</div>
                                    <div style={{ 
                                        fontSize: '14px', 
                                        fontWeight: '600', 
                                        color: (accountCash.result || 0) >= 0 ? '#28a745' : '#dc3545' 
                                    }}>
                                        {accountCash.currency} {accountCash.result?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Pie Cash</div>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                                        {accountCash.currency} {accountCash.pieCash?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                {accountCashLoading ? 'Loading ISA data...' : 'ISA data not available'}
                            </div>
                        )}
                    </div>

                    {/* CFD Account Display */}
                    <div style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            padding: '10px 20px',
                            fontWeight: 'bold',
                            textAlign: 'center'
                        }}>
                            CFD Account
                        </div>
                        {cfdSummary && cfdSummary.cash ? (
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                                gap: '15px',
                                padding: '20px'
                            }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Free Cash</div>
                                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#28a745' }}>
                                        ${cfdSummary.cash.free?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Total Cash</div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#007bff' }}>
                                        ${cfdSummary.cash.total?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>P&L</div>
                                    <div style={{ 
                                        fontSize: '14px', 
                                        fontWeight: '600', 
                                        color: (cfdSummary.cash.ppl || 0) >= 0 ? '#28a745' : '#dc3545' 
                                    }}>
                                        ${cfdSummary.cash.ppl?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Margin</div>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                                        ${cfdSummary.cash.margin?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Result</div>
                                    <div style={{ 
                                        fontSize: '14px', 
                                        fontWeight: '600', 
                                        color: (cfdSummary.cash.result || 0) >= 0 ? '#28a745' : '#dc3545' 
                                    }}>
                                        ${cfdSummary.cash.result?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Indicator</div>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                                        ${cfdSummary.cash.indicator?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                {accountCashLoading ? 'Loading CFD data...' : 'CFD data not available'}
                            </div>
                        )}
                    </div>
                </div>

                {/* CFD Error Display */}
                {cfdError && (
                    <div style={{
                        marginTop: '20px',
                        padding: '15px',
                        backgroundColor: '#f8d7da',
                        color: '#721c24',
                        border: '1px solid #f5c6cb',
                        borderRadius: '4px',
                        fontSize: '14px'
                    }}>
                        <strong>CFD Error:</strong> {cfdError}
                    </div>
                )}
            </div>

            {/* Stock Analysis Section */}
            <div>
                <h2 style={{ color: '#333', marginBottom: '15px', fontSize: '20px' }}>Stock Analysis</h2>
                <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                    Enter a ticker symbol to fetch key statistics and stock price information.
                </p>
                
                <div style={{ 
                    display: 'flex', 
                    gap: '10px', 
                    marginBottom: '30px',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    <input
                        type="text"
                        placeholder="Enter ticker symbol (e.g., AAPL, TSLA)"
                        value={ticker}
                        onChange={(e) => setTicker(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={loading}
                        style={{
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '16px',
                            minWidth: '250px',
                            flex: '1'
                        }}
                    />
                    <button
                        onClick={handleFetchStockData}
                        disabled={loading || !ticker.trim()}
                        style={{
                            backgroundColor: (loading || !ticker.trim()) ? '#ccc' : '#007bff',
                            color: 'white',
                            padding: '10px 20px',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (loading || !ticker.trim()) ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {loading ? 'Fetching...' : 'Get Stock Data'}
                    </button>
                </div>
            </div>

            {/* Place Order Section - Split Layout */}
            <div style={{ marginBottom: '40px' }}>
                <h2 style={{ color: '#333', marginBottom: '15px', fontSize: '20px' }}>Place Order</h2>
                <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                    Enter a ticker symbol and place orders on ISA or CFD accounts.
                </p>
                
                {/* Split layout for ISA and CFD orders */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                    
                    {/* Left Section - ISA Orders */}
                    <div style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            padding: '15px 20px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            borderRadius: '7px 7px 0 0'
                        }}>
                            ISA Orders
                        </div>
                        
                        <div style={{ padding: '20px' }}>
                            {/* ISA Order Input */}
                            <div style={{ 
                                display: 'flex', 
                                gap: '10px', 
                                marginBottom: '20px',
                                alignItems: 'center',
                                flexWrap: 'wrap'
                            }}>
                                <input
                                    type="number"
                                    placeholder="Enter value amount (£)"
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                    disabled={placeOrderLoading}
                                    min="0"
                                    step="0.01"
                                    style={{
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '16px',
                                        flex: '1',
                                        minWidth: '150px'
                                    }}
                                />
                                <button
                                    onClick={handlePlaceOrder}
                                    disabled={placeOrderLoading || !ticker.trim() || !value.trim()}
                                    style={{
                                        backgroundColor: (placeOrderLoading || !ticker.trim() || !value.trim()) ? '#ccc' : '#007bff',
                                        color: 'white',
                                        padding: '10px 15px',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: (placeOrderLoading || !ticker.trim() || !value.trim()) ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {placeOrderLoading ? 'Placing...' : 'Place Order'}
                                </button>
                            </div>

                            {/* ISA Fraction Order Buttons */}
                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ color: '#333', marginBottom: '10px', fontSize: '14px' }}>Fraction Orders</h4>
                                <p style={{ color: '#666', marginBottom: '15px', fontSize: '12px' }}>
                                    Place orders for fractions of your free cash
                                </p>
                                <div style={{ 
                                    display: 'flex', 
                                    gap: '6px', 
                                    flexWrap: 'wrap'
                                }}>
                                    <button
                                        onClick={() => handleFractionOrder(1/5, '1/5')}
                                        disabled={!ticker.trim() || placeOrderLoading}
                                        style={{
                                            backgroundColor: (!ticker.trim() || placeOrderLoading) ? '#ccc' : '#17a2b8',
                                            color: 'white',
                                            padding: '6px 12px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: (!ticker.trim() || placeOrderLoading) ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        {placeOrderLoading ? '...' : '1/5'}
                                    </button>
                                    <button
                                        onClick={() => handleFractionOrder(1/4, '1/4')}
                                        disabled={!ticker.trim() || placeOrderLoading}
                                        style={{
                                            backgroundColor: (!ticker.trim() || placeOrderLoading) ? '#ccc' : '#17a2b8',
                                            color: 'white',
                                            padding: '6px 12px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: (!ticker.trim() || placeOrderLoading) ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        {placeOrderLoading ? '...' : '1/4'}
                                    </button>
                                    <button
                                        onClick={() => handleFractionOrder(1/3, '1/3')}
                                        disabled={!ticker.trim() || placeOrderLoading}
                                        style={{
                                            backgroundColor: (!ticker.trim() || placeOrderLoading) ? '#ccc' : '#17a2b8',
                                            color: 'white',
                                            padding: '6px 12px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: (!ticker.trim() || placeOrderLoading) ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        {placeOrderLoading ? '...' : '1/3'}
                                    </button>
                                    <button
                                        onClick={() => handleFractionOrder(1/2, '1/2')}
                                        disabled={!ticker.trim() || placeOrderLoading}
                                        style={{
                                            backgroundColor: (!ticker.trim() || placeOrderLoading) ? '#ccc' : '#17a2b8',
                                            color: 'white',
                                            padding: '6px 12px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: (!ticker.trim() || placeOrderLoading) ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        {placeOrderLoading ? '...' : '1/2'}
                                    </button>
                                    <button
                                        onClick={() => handleFractionOrder(0.995, 'All In')}
                                        disabled={!ticker.trim() || placeOrderLoading}
                                        style={{
                                            backgroundColor: (!ticker.trim() || placeOrderLoading) ? '#ccc' : '#fd7e14',
                                            color: 'white',
                                            padding: '6px 12px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: (!ticker.trim() || placeOrderLoading) ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        {placeOrderLoading ? '...' : 'All In'}
                                    </button>
                                </div>
                            </div>

                            {/* ISA Order Error */}
                            {placeOrderError && (
                                <div style={{
                                    marginBottom: '10px',
                                    padding: '10px',
                                    backgroundColor: '#f8d7da',
                                    color: '#721c24',
                                    border: '1px solid #f5c6cb',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                }}>
                                    <strong>Error:</strong> {placeOrderError}
                                </div>
                            )}

                            {/* ISA Order Response */}
                            {orderResponse && (
                                <div style={{
                                    padding: '10px',
                                    backgroundColor: '#d4edda',
                                    color: '#155724',
                                    border: '1px solid #c3e6cb',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                }}>
                                    ✅ ISA Order placed successfully!
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Section - CFD Orders */}
                    <div style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            padding: '15px 20px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            borderRadius: '7px 7px 0 0'
                        }}>
                            CFD Orders
                        </div>
                        
                        <div style={{ padding: '20px' }}>
                            {/* CFD Order Input */}
                            <div style={{ 
                                display: 'flex', 
                                gap: '10px', 
                                marginBottom: '20px',
                                alignItems: 'center',
                                flexWrap: 'wrap'
                            }}>
                                <input
                                    type="number"
                                    placeholder="Enter quantity"
                                    value={cfdQuantity}
                                    onChange={(e) => setCfdQuantity(e.target.value)}
                                    disabled={cfdOrderLoading}
                                    min="0"
                                    step="0.01"
                                    style={{
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '16px',
                                        flex: '1',
                                        minWidth: '150px'
                                    }}
                                />
                                <button
                                    onClick={handleCfdPlaceOrder}
                                    disabled={cfdOrderLoading || !ticker.trim() || !cfdQuantity.trim()}
                                    style={{
                                        backgroundColor: (cfdOrderLoading || !ticker.trim() || !cfdQuantity.trim()) ? '#ccc' : '#28a745',
                                        color: 'white',
                                        padding: '10px 15px',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: (cfdOrderLoading || !ticker.trim() || !cfdQuantity.trim()) ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {cfdOrderLoading ? 'Placing...' : 'Place Order'}
                                </button>
                            </div>

                            {/* CFD Fraction Order Buttons */}
                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ color: '#333', marginBottom: '10px', fontSize: '14px' }}>Fraction Orders</h4>
                                <p style={{ color: '#666', marginBottom: '15px', fontSize: '12px' }}>
                                    Place orders for fractions of max buy amount
                                </p>
                                <div style={{ 
                                    display: 'flex', 
                                    gap: '6px', 
                                    flexWrap: 'wrap'
                                }}>
                                    <button
                                        onClick={() => handleCfdFractionOrder(1/5, '1/5')}
                                        disabled={!ticker.trim() || cfdOrderLoading}
                                        style={{
                                            backgroundColor: (!ticker.trim() || cfdOrderLoading) ? '#ccc' : '#17a2b8',
                                            color: 'white',
                                            padding: '6px 12px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: (!ticker.trim() || cfdOrderLoading) ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        {cfdOrderLoading ? '...' : '1/5'}
                                    </button>
                                    <button
                                        onClick={() => handleCfdFractionOrder(1/4, '1/4')}
                                        disabled={!ticker.trim() || cfdOrderLoading}
                                        style={{
                                            backgroundColor: (!ticker.trim() || cfdOrderLoading) ? '#ccc' : '#17a2b8',
                                            color: 'white',
                                            padding: '6px 12px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: (!ticker.trim() || cfdOrderLoading) ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        {cfdOrderLoading ? '...' : '1/4'}
                                    </button>
                                    <button
                                        onClick={() => handleCfdFractionOrder(1/3, '1/3')}
                                        disabled={!ticker.trim() || cfdOrderLoading}
                                        style={{
                                            backgroundColor: (!ticker.trim() || cfdOrderLoading) ? '#ccc' : '#17a2b8',
                                            color: 'white',
                                            padding: '6px 12px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: (!ticker.trim() || cfdOrderLoading) ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        {cfdOrderLoading ? '...' : '1/3'}
                                    </button>
                                    <button
                                        onClick={() => handleCfdFractionOrder(1/2, '1/2')}
                                        disabled={!ticker.trim() || cfdOrderLoading}
                                        style={{
                                            backgroundColor: (!ticker.trim() || cfdOrderLoading) ? '#ccc' : '#17a2b8',
                                            color: 'white',
                                            padding: '6px 12px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: (!ticker.trim() || cfdOrderLoading) ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        {cfdOrderLoading ? '...' : '1/2'}
                                    </button>
                                    <button
                                        onClick={() => handleCfdFractionOrder(0.995, 'All In')}
                                        disabled={!ticker.trim() || cfdOrderLoading}
                                        style={{
                                            backgroundColor: (!ticker.trim() || cfdOrderLoading) ? '#ccc' : '#fd7e14',
                                            color: 'white',
                                            padding: '6px 12px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: (!ticker.trim() || cfdOrderLoading) ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        {cfdOrderLoading ? '...' : 'All In'}
                                    </button>
                                </div>
                            </div>

                            {/* CFD Order Error */}
                            {cfdOrderError && (
                                <div style={{
                                    marginBottom: '10px',
                                    padding: '10px',
                                    backgroundColor: '#f8d7da',
                                    color: '#721c24',
                                    border: '1px solid #f5c6cb',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                }}>
                                    <strong>Error:</strong> {cfdOrderError}
                                </div>
                            )}

                            {/* CFD Order Response */}
                            {cfdOrderResponse && (
                                <div style={{
                                    padding: '10px',
                                    backgroundColor: '#d4edda',
                                    color: '#155724',
                                    border: '1px solid #c3e6cb',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                }}>
                                    ✅ CFD Order placed successfully!
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {error && (
                <div style={{
                    marginBottom: '20px',
                    padding: '15px',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px',
                    fontSize: '14px'
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Stock Price Display */}
            {stockPrice && (
                <div style={{ marginTop: '30px' }}>
                    <h2 style={{ color: '#333', marginBottom: '20px' }}>
                        Stock Price for {ticker.toUpperCase()}
                    </h2>
                    
                    <div style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                            gap: '20px',
                            padding: '20px'
                        }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Close Price</div>
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                                    {stockPrice.close?.toLocaleString() || 'N/A'}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Market Session</div>
                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#007bff' }}>
                                    {stockPrice.marketSession || 'N/A'}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Timestamp</div>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                                    {stockPrice.timestamp || 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {keyStats.length > 0 && (
                <div style={{ marginTop: '30px' }}>
                    <h2 style={{ color: '#333', marginBottom: '20px' }}>
                        Key Statistics for {ticker.toUpperCase()} ({keyStats.length} metrics)
                    </h2>
                    
                    <div style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <table style={{ 
                            width: '100%', 
                            borderCollapse: 'collapse',
                            fontSize: '14px'
                        }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{
                                        padding: '15px',
                                        textAlign: 'left',
                                        borderBottom: '1px solid #ddd',
                                        fontWeight: '600',
                                        color: '#333'
                                    }}>
                                        Statistic
                                    </th>
                                    <th style={{
                                        padding: '15px',
                                        textAlign: 'right',
                                        borderBottom: '1px solid #ddd',
                                        fontWeight: '600',
                                        color: '#333'
                                    }}>
                                        Value
                                    </th>
                                    <th style={{
                                        padding: '15px',
                                        textAlign: 'center',
                                        borderBottom: '1px solid #ddd',
                                        fontWeight: '600',
                                        color: '#333'
                                    }}>
                                        Type
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {keyStats.map((stat, index) => (
                                    <tr 
                                        key={`${stat.title}-${index}`}
                                        style={{ 
                                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                            transition: 'background-color 0.2s ease'
                                        }}
                                    >
                                        <td style={{
                                            padding: '15px',
                                            borderBottom: index === keyStats.length - 1 ? 'none' : '1px solid #eee',
                                            fontWeight: '500',
                                            color: '#333'
                                        }}>
                                            {stat.title}
                                        </td>
                                        <td style={{
                                            padding: '15px',
                                            textAlign: 'right',
                                            borderBottom: index === keyStats.length - 1 ? 'none' : '1px solid #eee',
                                            fontWeight: '600',
                                            color: stat.value?.value !== undefined ? '#28a745' : '#6c757d'
                                        }}>
                                            {formatValue(stat.value)}
                                        </td>
                                        <td style={{
                                            padding: '15px',
                                            textAlign: 'center',
                                            borderBottom: index === keyStats.length - 1 ? 'none' : '1px solid #eee',
                                            fontSize: '12px',
                                            color: '#666'
                                        }}>
                                            {stat.value?.type || 'N/A'}
                                            {stat.value?.compact && (
                                                <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                                                    Compact
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!loading && !error && keyStats.length === 0 && ticker && (
                <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: '#d1ecf1',
                    color: '#0c5460',
                    border: '1px solid #bee5eb',
                    borderRadius: '4px',
                    fontSize: '14px'
                }}>
                    No key statistics found for ticker "{ticker.toUpperCase()}". 
                    Please verify the ticker symbol and ensure Trading212 credentials are configured.
                </div>
            )}

            {!loading && !error && keyStats.length === 0 && !ticker && (
                <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    color: '#495057',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px'
                }}>
                    Enter a ticker symbol above to fetch key statistics from Trading212.
                </div>
            )}
        </div>
    );
};
